import { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Button,
    Typography,
} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchableDropdown from "../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx";

const BUSINESS_TYPES = [
    { label: "Startup", value: "Startup" },
    {
        label: "SME (Small & Medium Enterprise)",
        value: "SME",
    },
    {
        label: "Mid-Market",
        value: "Mid-Market",
    },
    {
        label: "Large Enterprise",
        value: "Large Enterprise",
    },
    {
        label: "MNC (Multinational Corporation)",
        value: "MNC",
    },
    {
        label: "Freelancer / Solo",
        value: "Freelancer / Solo",
    },
    {
        label: "Non-Profit / NGO",
        value: "Non-Profit / NGO",
    },
    {
        label: "Government / Public Sector",
        value: "Government / Public Sector",
    },
    {
        label: "Agency / Consultancy",
        value: "Agency / Consultancy",
    },
];

const EMPLOYEE_OPTIONS = [
    { label: "1 - 10 Employees", value: "1-10" },
    { label: "11 - 50 Employees", value: "11-50" },
    { label: "51 - 200 Employees", value: "51-200" },
    { label: "201 - 500 Employees", value: "201-500" },
    { label: "501 - 1000 Employees", value: "501-1000" },
    { label: "1000 - 5000 Employees", value: "1000-5000" },
    { label: "5000+ Employees", value: "5000+" },
];

const LICENSE_TYPES = [
    { label: "Starter", value: "Starter" },
    { label: "Professional", value: "Professional" },
    { label: "Business", value: "Business" },
    { label: "Enterprise", value: "Enterprise" },
    { label: "Custom/Negotiated", value: "Custom/Negotiated" },
];

export default function StepBusinessDetails({
    formData,
    updateData,
    onNext,
    onBack,
}) {
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const nextFields = {};

        if (
            formData?.businessType &&
            !formData?.businessTypeData
        ) {
            const matchedBusinessType =
                BUSINESS_TYPES.find(
                    (item) =>
                        item.value ===
                        formData.businessType
                ) || null;

            if (matchedBusinessType) {
                nextFields.businessTypeData =
                    matchedBusinessType;
            }
        }

        if (
            formData?.numEmployees &&
            !formData?.numEmployeesData
        ) {
            const matchedEmployees =
                EMPLOYEE_OPTIONS.find(
                    (item) =>
                        item.value ===
                        formData.numEmployees
                ) || null;

            if (matchedEmployees) {
                nextFields.numEmployeesData =
                    matchedEmployees;
            }
        }

        if (
            formData?.licenseType &&
            !formData?.licenseTypeData
        ) {
            const matchedLicenseType =
                LICENSE_TYPES.find(
                    (item) =>
                        item.value ===
                        formData.licenseType
                ) || null;

            if (matchedLicenseType) {
                nextFields.licenseTypeData =
                    matchedLicenseType;
            }
        }

        if (Object.keys(nextFields).length) {
            updateData(nextFields);
        }
    }, [
        formData?.businessType,
        formData?.businessTypeData,
        formData?.numEmployees,
        formData?.numEmployeesData,
        formData?.licenseType,
        formData?.licenseTypeData,
        updateData,
    ]);

    const validate = () => {
        const e = {};

        if (!formData?.businessType) {
            e.businessType =
                "Please select a business type";
        }

        if (!formData?.numEmployees) {
            e.numEmployees =
                "Please select team size";
        }

        if (!formData?.licenseType) {
            e.licenseType =
                "Please select licence type";
        }

        return e;
    };

    const handleNext = () => {
        const e = validate();

        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        setErrors({});
        onNext();
    };

    return (
        <Box>
            {/* Title */}
            <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                letterSpacing="0.1em"
            >
                Step 3 of 4
            </Typography>

            <Typography
                variant="h5"
                fontWeight={700}
                mt={0.5}
                mb={0.5}
            >
                A bit more about your{" "}
                <Box
                    component="span"
                    color="primary.main"
                >
                    operations
                </Box>
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                mb={3}
            >
                These details help us calibrate
                tone, scale and use-case
                recommendations.
            </Typography>

            {/* Dropdowns */}
            <Box mt={2}>
                <Grid container spacing={2}>
                    {/* Business Type */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Type of business *"
                            options={
                                BUSINESS_TYPES
                            }
                            value={
                                formData?.businessTypeData ||
                                null
                            }
                            onChange={(val) =>
                                updateData({
                                    businessType:
                                        val?.value ||
                                        "",
                                    businessTypeData:
                                        val ||
                                        null,
                                })
                            }
                            labelKey="label"
                            valueKey="value"
                            size="small"
                            error={
                                !!errors?.businessType
                            }
                            errorMessage={
                                errors?.businessType
                            }
                        />
                    </Grid>

                    {/* Employees */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Number of employees *"
                            options={
                                EMPLOYEE_OPTIONS
                            }
                            value={
                                formData?.numEmployeesData ||
                                null
                            }
                            onChange={(val) =>
                                updateData({
                                    numEmployees:
                                        val?.value ||
                                        "",
                                    numEmployeesData:
                                        val ||
                                        null,
                                })
                            }
                            labelKey="label"
                            valueKey="value"
                            size="small"
                            error={
                                !!errors?.numEmployees
                            }
                            errorMessage={
                                errors?.numEmployees
                            }
                        />
                    </Grid>

                    {/* Licence */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Licence type *"
                            options={
                                LICENSE_TYPES
                            }
                            value={
                                formData?.licenseTypeData ||
                                null
                            }
                            onChange={(val) =>
                                updateData({
                                    licenseType:
                                        val?.value ||
                                        "",
                                    licenseTypeData:
                                        val ||
                                        null,
                                })
                            }
                            labelKey="label"
                            valueKey="value"
                            size="small"
                            error={
                                !!errors?.licenseType
                            }
                            errorMessage={
                                errors?.licenseType
                            }
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Footer */}
            <Box
                display="flex"
                justifyContent="space-between"
                mt={4}
                pt={2.5}
                borderTop="1px solid"
                borderColor="grey.100"
            >
                <Button
                    variant="outlined"
                    startIcon={
                        <ArrowBackIcon />
                    }
                    onClick={onBack}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    Back
                </Button>

                <Button
                    variant="contained"
                    endIcon={
                        <ArrowForwardIcon />
                    }
                    onClick={handleNext}
                    disableElevation
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                    }}
                >
                    Next: Brand
                </Button>
            </Box>
        </Box>
    );
}
